import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Video, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader, Loading, EmptyState, Avatar, Tag, Chip } from "@/components/brand";
import { canJoin, formatDayTime, formatR, isPast } from "@/lib/format";
import { cn } from "@/lib/utils";

const FILTERS = [["upcoming", "Upcoming"], ["past", "Past"], ["all", "All"]];

export default function Bookings() {
  const [filter, setFilter] = useState("upcoming");
  const bookings = useQuery({ queryKey: ["bookings", "me"], queryFn: () => api("/bookings/me") });
  const all = bookings.data ?? [];
  const list = all.filter((b) => {
    const past = isPast(b.start_time, b.duration_min) || ["completed", "cancelled", "refunded"].includes(b.status);
    if (filter === "upcoming") return !past;
    if (filter === "past") return past;
    return true;
  });

  const statusTone = (s) => s === "completed" ? "green" : s === "confirmed" ? "pink" : s === "pending_payment" ? "dark" : "grey";

  return (
    <div data-testid="bookings-screen" className="space-y-5">
      <SectionHeader title="My Bookings" subtitle="Your live classes and private sessions." />
      <div className="flex gap-2">{FILTERS.map(([k, l]) => <Chip key={k} label={l} selected={filter === k} onClick={() => setFilter(k)} testID={`bookings-filter-${k}`} />)}</div>
      {bookings.isLoading ? <Loading /> : list.length === 0 ? (
        <EmptyState icon={Video} title="No bookings here" subtitle="Book a class or a private session to get started." />
      ) : (
        <div className="space-y-3">
          {list.map((b) => {
            const joinable = b.status === "confirmed" && canJoin(b.start_time, b.duration_min);
            return (
              <Link key={b.id} to={joinable ? `/live/${b.id}` : `/booking/${b.id}`} data-testid={`booking-row-${b.id}`}
                className="flex items-center gap-4 p-4 rounded-xl2 bg-white border border-divider hover:border-brand transition-colors">
                {b.image ? <img src={b.image} alt="" className="w-16 h-16 rounded-md object-cover" /> : <div className="w-16 h-16 rounded-md bg-brand-soft" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Tag label={b.type === "private" ? "Private" : "Group"} tone={b.type === "private" ? "dark" : "pink"} />
                    <Tag label={b.status === "pending_payment" ? "awaiting payment" : b.status} tone={statusTone(b.status)} />
                  </div>
                  <div className="font-display text-lg text-ink truncate mt-1">{b.title}</div>
                  <div className="font-body text-[13px] text-mutedgrey">with {b.trainer_name} · {formatDayTime(b.start_time)}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {joinable ? <span className="inline-flex items-center gap-1 h-8 px-3 rounded-pill bg-brand text-white font-body font-semibold text-xs"><Video size={14} />Join</span> : <span className="font-display text-brand">{formatR(b.price)}</span>}
                  <ChevronRight size={18} className="text-mutedgrey" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
